import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { PageTitle, SideIcon } from "@/components/shared";
import { formatRupiah, parseNumber } from "@/lib/format";
import { isLunas, isBought } from "@/lib/compute";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { Heart } from "lucide-react";
import { isBackupDue, exportBackup, snoozeBackup } from "@/lib/backup";
import { toast } from "sonner";

function StatCard({ label, value, pct, delay }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.5 }}>
      <Card className="p-6">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        {value !== undefined && <p className="mt-2 font-heading text-2xl font-semibold">{value}</p>}
        {pct !== undefined && (
          <div className="mt-3">
            <div className="flex justify-between text-sm"><span>{pct}%</span></div>
            <Progress value={pct} className="mt-1.5 h-2" />
          </div>
        )}
      </Card>
    </motion.div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { data, activePasangan, selectPasangan, pasanganInfo } = useData();
  const isAdmin = user?.role === "sysadmin" || user?.role === "Admin";
  const [pasanganList, setPasanganList] = useState([]);
  const [backupDue, setBackupDue] = useState(isBackupDue());
  const pid = isAdmin ? activePasangan : user?.pasangan_id;
  const [weddingDate, setWeddingDate] = useState("");
  const [savedDate, setSavedDate] = useState("");

  useEffect(() => { if (isAdmin) api.get("/pasangan").then((r) => setPasanganList(r.data)).catch(() => {}); }, [isAdmin]);

  useEffect(() => {
    if (!activePasangan && isAdmin) return;
    api.get("/settings", { params: isAdmin ? { pasangan_id: activePasangan } : {} })
      .then((r) => { setWeddingDate(r.data.wedding_date || ""); setSavedDate(r.data.wedding_date || ""); })
      .catch(() => {});
  }, [activePasangan, isAdmin]);

  const saveDate = async () => {
    await api.put("/settings", { wedding_date: weddingDate }, { params: isAdmin ? { pasangan_id: activePasangan } : {} });
    setSavedDate(weddingDate); toast.success("Tanggal pernikahan disimpan");
  };

  const tabungan = data.tabungan || [];
  const budget = data.budget || [];
  const seserahan = data.seserahan || [];
  const life = data.life_after_married || [];
  const dokumen = data.dokumen || [];
  const todo = data.todo || [];
  const payments = data.pembayaran || [];
  const tamu = data.tamu || [];

  const totalTabungan = tabungan.reduce((s, i) => s + parseNumber(i.nominal), 0);
  const totalPria = tabungan.filter((i) => i.oleh === "Pria").reduce((s, i) => s + parseNumber(i.nominal), 0);
  const totalWanita = tabungan.filter((i) => i.oleh === "Wanita").reduce((s, i) => s + parseNumber(i.nominal), 0);
  const totalBudget = budget.reduce((s, i) => s + parseNumber(i.nominal), 0);
  const totalSeserahan = seserahan.reduce((s, i) => s + parseNumber(i.estimasi), 0);
  const totalLife = life.reduce((s, i) => s + parseNumber(i.harga), 0);
  const target = totalBudget + totalSeserahan + totalLife;
  const pctTabungan = target > 0 ? Math.min(100, Math.round((totalTabungan / target) * 100)) : 0;

  const pct = (done, total) => (total > 0 ? Math.round((done / total) * 100) : 0);
  const pctDok = pct(dokumen.filter((d) => d.done).length, dokumen.length);
  const pctTodo = pct(todo.filter((t) => t.done).length, todo.length);
  const pctSeserahan = pct(seserahan.filter((s) => isBought(s, payments, "seserahan")).length, seserahan.length);

  const totalPembayaran = payments.reduce((s, i) => s + parseNumber(i.nominal), 0);
  const saldo = totalTabungan - totalPembayaran;
  const tamuPria = tamu.filter((t) => t.tamu_dari === "Pria").length;
  const tamuWanita = tamu.filter((t) => t.tamu_dari === "Wanita").length;

  const days = useMemo(() => {
    if (!savedDate) return null;
    const d = new Date(savedDate + "T00:00:00");
    const diff = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  }, [savedDate]);

  const chartData = useMemo(() => {
    const map = {};
    tabungan.forEach((t) => {
      const m = (t.tanggal || "").slice(0, 7) || "N/A";
      map[m] = map[m] || { bulan: m, Pria: 0, Wanita: 0 };
      map[m][t.oleh === "Wanita" ? "Wanita" : "Pria"] += parseNumber(t.nominal);
    });
    return Object.values(map).sort((a, b) => a.bulan.localeCompare(b.bulan));
  }, [tabungan]);

  return (
    <div>
      <PageTitle title="Home" subtitle="Ringkasan persiapan pernikahanmu" />

      {backupDue && (
        <Card className="mb-6 flex flex-wrap items-center justify-between gap-3 border-primary/40 bg-primary/5 p-4" data-testid="backup-reminder">
          <p className="text-sm">Sudah lebih dari seminggu sejak backup terakhir. Amankan datamu secara berkala.</p>
          <div className="flex gap-2">
            <Button size="sm" data-testid="backup-now" onClick={() => { exportBackup(data, pid); setBackupDue(false); }}>Backup Sekarang</Button>
            <Button size="sm" variant="ghost" data-testid="backup-snooze" onClick={() => { snoozeBackup(); setBackupDue(false); toast.info("Diingatkan lagi besok"); }}>Nanti</Button>
          </div>
        </Card>
      )}

      {isAdmin && (
        <Card className="mb-6 p-5">
          <p className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">Pilih Pasangan</p>
          <Select value={activePasangan || ""} onValueChange={selectPasangan}>
            <SelectTrigger className="max-w-sm" data-testid="pasangan-selector"><SelectValue placeholder="Pilih pasangan..." /></SelectTrigger>
            <SelectContent>
              {pasanganList.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama} ({p.id})</SelectItem>)}
            </SelectContent>
          </Select>
        </Card>
      )}

      {/* Countdown + date */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Heart className="h-3.5 w-3.5 fill-red-500 text-red-500" /> Menuju Hari Bahagia</p>
              <p className="mt-2 font-heading text-6xl font-light tracking-tighter" data-testid="countdown-value">
                {days === null ? "—" : days >= 0 ? days : 0}
                <span className="ml-2 text-xl text-muted-foreground">hari</span>
              </p>
              {pasanganInfo && <p className="mt-1 text-sm text-muted-foreground"><SideIcon side="Pria" /> {pasanganInfo.pria?.username || "Pria"} & <SideIcon side="Wanita" /> {pasanganInfo.wanita?.username || "Wanita"}</p>}
            </div>
            <div className="flex items-end gap-2">
              <div>
                <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Tanggal Pernikahan</p>
                <Input type="date" data-testid="wedding-date-input" value={weddingDate} onChange={(e) => setWeddingDate(e.target.value)} className="w-44" />
              </div>
              <Button data-testid="save-wedding-date" onClick={saveDate}>Simpan</Button>
            </div>
          </div>
        </Card>
        <StatCard label="Total Tabungan Target" value={formatRupiah(target)} pct={pctTabungan} delay={0.1} />
      </div>

      {/* Progress stats */}
      <div className="mb-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Persentase Tabungan" pct={pctTabungan} delay={0.05} />
        <StatCard label="Penyelesaian Dokumen" pct={pctDok} delay={0.1} />
        <StatCard label="Penyelesaian To Do" pct={pctTodo} delay={0.15} />
        <StatCard label="Pembelian Seserahan" pct={pctSeserahan} delay={0.2} />
      </div>

      {/* Savings by side + guests */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-6">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Tabungan Pria</p>
          <p className="mt-2 font-heading text-2xl font-semibold"><SideIcon side="Pria" /> {formatRupiah(totalPria)}</p>
        </Card>
        <Card className="p-6">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Tabungan Wanita</p>
          <p className="mt-2 font-heading text-2xl font-semibold"><SideIcon side="Wanita" /> {formatRupiah(totalWanita)}</p>
        </Card>
        <Card className="p-6">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Jumlah Tamu</p>
          <p className="mt-2 text-sm"><SideIcon side="Pria" /> Pria: <b>{tamuPria}</b></p>
          <p className="text-sm"><SideIcon side="Wanita" /> Wanita: <b>{tamuWanita}</b></p>
          <p className="mt-1 text-sm text-muted-foreground">Total: <b>{tamu.length}</b></p>
        </Card>
      </div>

      {/* Chart */}
      <Card className="mb-6 p-6">
        <p className="mb-4 text-xs uppercase tracking-wider text-muted-foreground">Grafik Tabungan Pria vs Wanita</p>
        <div style={{ width: "100%", height: 300 }} data-testid="savings-chart">
          <ResponsiveContainer>
            <BarChart data={chartData.length ? chartData : [{ bulan: "-", Pria: 0, Wanita: 0 }]}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="bulan" fontSize={12} />
              <YAxis fontSize={12} tickFormatter={(v) => (v >= 1000000 ? `${v / 1000000}jt` : v)} />
              <Tooltip formatter={(v) => formatRupiah(v)} />
              <Legend />
              <Bar dataKey="Pria" fill="var(--brand)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Wanita" fill="#f06595" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Saldo table */}
      <Card className="p-6">
        <p className="mb-4 text-xs uppercase tracking-wider text-muted-foreground">Ringkasan Saldo</p>
        <Table data-testid="saldo-table">
          <TableHeader><TableRow>
            <TableHead>Total Tabungan</TableHead><TableHead>Total Pembayaran</TableHead><TableHead>Saldo Total</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">{formatRupiah(totalTabungan)}</TableCell>
              <TableCell className="font-medium">{formatRupiah(totalPembayaran)}</TableCell>
              <TableCell className={`font-semibold ${saldo < 0 ? "text-red-500" : "text-green-600"}`}>{formatRupiah(saldo)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
